import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { AuthProvider, useAuth } from './AuthContext';
import LoginForm from './LoginForm';

const WellnessTrackerContent = () => {
  const { user, logout } = useAuth();
  const [currentView, setCurrentView] = useState('home');
  const [currentSection, setCurrentSection] = useState('spiritual');
  const [weekOf, setWeekOf] = useState('');
  const [goals, setGoals] = useState('');
  const [checkedItems, setCheckedItems] = useState({});
  const [currentWeek, setCurrentWeek] = useState(0);
  const [workoutChecked, setWorkoutChecked] = useState({});

  // Load user-specific data from localStorage on mount
  useEffect(() => {
    if (user) {
      const savedData = localStorage.getItem(`wellnessTracker_${user.id}`);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        setCheckedItems(parsed.checkedItems || {});
        setWeekOf(parsed.weekOf || '');
        setGoals(parsed.goals || '');
        setCurrentWeek(parsed.currentWeek || 0);
        setWorkoutChecked(parsed.workoutChecked || {});
      }
    }
  }, [user]);

  // Save user-specific data to localStorage whenever state changes (debounced for text inputs)
  useEffect(() => {
    if (user) {
      const dataToSave = {
        checkedItems,
        weekOf,
        goals,
        currentWeek,
        workoutChecked,
      };
      
      // Immediate save for checkboxes and selects, debounced for text inputs
      const timer = setTimeout(() => {
        localStorage.setItem(`wellnessTracker_${user.id}`, JSON.stringify(dataToSave));
      }, 300); // 300ms debounce
      
      return () => clearTimeout(timer);
    }
  }, [checkedItems, weekOf, goals, currentWeek, workoutChecked, user]);

  const days = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

  const toggleCheck = (section, activity, day) => {
    const key = `${currentWeek}-${section}-${activity}-${day}`;
    setCheckedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const isChecked = (section, activity, day) => {
    const key = `${currentWeek}-${section}-${activity}-${day}`;
    return checkedItems[key] || false;
  };

  const toggleWorkoutCheck = useCallback((exerciseIdx) => {
    const key = `${currentWeek}-workout-${exerciseIdx}`;
    setWorkoutChecked((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, [currentWeek]);

  const isWorkoutChecked = useCallback((exerciseIdx) => {
    const key = `${currentWeek}-workout-${exerciseIdx}`;
    return workoutChecked[key] || false;
  }, [currentWeek, workoutChecked]);

  const handleGoalsChange = useCallback((e) => {
    setGoals(e.target.value);
  }, []);

  const handleWeekOfChange = useCallback((e) => {
    setWeekOf(e.target.value);
  }, []);

  const activities = {
    spiritual: [
      { name: 'Daily Devotional/Prayer', days: [0, 1, 2, 3, 4, 5, 6] },
      { name: 'Box Breathing/Prayer Time', days: [0, 1, 2, 3, 4, 5, 6] },
      { name: 'Sabbath Rest', days: [6] },
    ],
    supplements: [
      { name: 'Black Coffee (AM Fasted)', days: [0, 1, 2, 3, 4, 5, 6] },
      { name: 'Maca Root (AM Fasted)', days: [0, 1, 2, 3, 4, 5, 6] },
      { name: 'Tongkat Ali 1600mg (AM Fasted)', days: [0, 1, 2, 3, 4, 5, 6] },
      { name: 'Horny Goat Weed (5 on/2 off)', days: [0, 1, 2, 3, 4] },
      { name: "Men's Multivitamin (AM)", days: [0, 1, 2, 3, 4, 5, 6] },
      { name: 'Vitamin D3 5000 IU (AM)', days: [0, 1, 2, 3, 4, 5, 6] },
      { name: 'Creatine (Pre/Post Workout)', days: [0, 1, 2, 3, 4, 5, 6] },
      { name: 'Citrulline with Malate (Pre-Workout)', days: [0, 1, 2, 3, 4, 5, 6] },
      { name: 'Magnesium (Evening)', days: [0, 1, 2, 3, 4, 5, 6] },
      { name: 'Zinc (Before Bed)', days: [0, 1, 2, 3, 4, 5, 6] },
    ],
    nutrition: [
      { name: 'Intermittent Fasting (16:8)', days: [0, 1, 2, 3, 4, 5, 6] },
      { name: 'Avoid Sugar/Alcohol', days: [0, 1, 2, 3, 4, 5, 6] },
    ],
    fitness: [
      { name: 'Push Strength + Rowing', days: [0] },
      { name: 'Pull Strength + Core', days: [1] },
      { name: 'Active Flow Yoga', days: [2] },
      { name: 'Legs + Core + Sprints', days: [3] },
      { name: 'Deep Stretch/Recovery Yoga', days: [4] },
    ],
    stretches: [
      { name: 'Hip Flexors (30s each)', days: [0, 1, 2, 3, 4, 5, 6] },
      { name: 'Hamstrings (30s each leg)', days: [0, 1, 2, 3, 4, 5, 6] },
      { name: 'Chest/Shoulders (Doorway 30s)', days: [0, 1, 2, 3, 4, 5, 6] },
      { name: 'Deep Squat Hold (30-60s)', days: [0, 1, 2, 3, 4, 5, 6] },
      { name: 'Neck/Trap (30s per side)', days: [0, 1, 2, 3, 4, 5, 6] },
    ],
  };

  const sectionConfig = {
    spiritual: { title: 'Spiritual', emoji: '🙏', color: '#10B981' },
    supplements: { title: 'Supplements', emoji: '💊', color: '#F59E0B' },
    nutrition: { title: 'Nutrition', emoji: '🥗', color: '#8B5CF6' },
    fitness: { title: 'Fitness', emoji: '💪', color: '#3B82F6' },
    stretches: { title: 'Stretches', emoji: '🧘‍♂️', color: '#EC4899' },
  };

  const workoutPlans = {
    0: { // Monday
      title: 'PUSH + ROWING',
      exercises: [
        'Shoulder Press - 3x10',
        'Chest Press - 3x10', 
        'Tricep Extension - 3x10',
        'Shoulder Cables - 3x10',
        'Chest Fly - 3x10',
        'Row Intervals - 5x (30s hard/30s easy)'
      ]
    },
    1: { // Tuesday
      title: 'PULL + CORE',
      exercises: [
        'DB Bent Over Rows - 12 reps',
        'Band Face Pulls - 15 reps',
        'KB Single-Arm Row - 8 per side',
        'Banded Curls - 15',
        'Plank - 45-60 sec',
        'Dead Bug/Bicycle - 15 per side',
        'Row 500m x2 (rest 1 min)'
      ]
    },
    2: { // Wednesday
      title: 'YOGA FLOW',
      exercises: [
        'Sun Salutations x3',
        'Warrior I/II → Side Angle',
        'Down Dog → Plank → Chaturanga',
        'Lizard Pose, Pigeon Pose',
        'Seated Forward Fold',
        'Box breathing/prayer time'
      ]
    },
    3: { // Thursday
      title: 'LEGS + SPRINTS',
      exercises: [
        'DB Goblet Squats - 12 reps',
        'KB Romanian Deadlifts - 10 reps',
        'DB Step-Ups/Lunges - 10 each',
        'Banded Glute Bridges - 15',
        'Russian Twists/Side Plank - 15/30s',
        'Row Sprint 100m x5 (30s rest)'
      ]
    },
    4: { // Friday
      title: 'RECOVERY YOGA',
      exercises: [
        'Child\'s Pose - 2 min',
        'Cat/Cow Flow',
        'Seated Twist, Spinal Twist',
        'Reclined Pigeon Pose',
        'Legs-Up-The-Wall - 3-5 min',
        '5-count breath prayer/stillness'
      ]
    }
  };

  const CheckBox = ({ checked, onToggle, disabled = false }) => (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`checkbox ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}`}
    >
      {checked && <span className="checkmark">✓</span>}
    </button>
  );

  const calculateProgress = (sectionKey) => {
    const sectionActivities = activities[sectionKey];
    let total = 0;
    let completed = 0;

    sectionActivities.forEach((activity, activityIdx) => {
      activity.days.forEach((dayIdx) => {
        total++;
        if (isChecked(sectionKey, activityIdx, dayIdx)) {
          completed++;
        }
      });
    });

    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };


  const HomeView = () => (
    <div className="container">
      <div className="header">
        <div className="header-content">
          <div>
            <h1 className="title">Wellness Tracker</h1>
            <p className="subtitle">Welcome, {user.username} • Week {currentWeek + 1}</p>
          </div>
          <button onClick={logout} className="logout-button">
            Logout
          </button>
        </div>
      </div>

      <div className="input-section">
        <div className="input-container">
          <label className="input-label">📅 Week of:</label>
          <input
            type="date"
            className="date-input"
            value={weekOf}
            onChange={handleWeekOfChange}
          />
        </div>

        <div className="input-container">
          <label className="input-label">🎯 Goals this week:</label>
          <textarea
            className="text-input goals-input"
            value={goals}
            onChange={handleGoalsChange}
            placeholder="What do you want to achieve?"
            rows={3}
          />
        </div>
      </div>

      <div className="progress-grid">
        {Object.entries(sectionConfig).map(([key, config]) => {
          const progress = calculateProgress(key);
          return (
            <div
              key={key}
              className="progress-card"
              style={{ borderColor: config.color }}
              onClick={() => {
                setCurrentSection(key);
                setCurrentView('tracker');
              }}
            >
              <div className="progress-header">
                <span className="emoji">{config.emoji}</span>
                <span className="progress-title">{config.title}</span>
              </div>
              <div className="progress-percent">{progress}%</div>
              <div className="progress-bar-container">
                <div
                  className="progress-bar"
                  style={{ width: `${progress}%`, backgroundColor: config.color }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Today's Workout */}
      {workoutPlans[new Date().getDay() - 1] && (
        <div className="todays-workout">
          <h3>Today's Workout</h3>
          <p>{workoutPlans[new Date().getDay() - 1].title}</p>
          <div className="workout-exercises">
            {workoutPlans[new Date().getDay() - 1].exercises.map((exercise, idx) => (
              <div key={idx} className="exercise-item">
                <CheckBox 
                  checked={isWorkoutChecked(idx)} 
                  onToggle={() => toggleWorkoutCheck(idx)} 
                />
                <span>{exercise}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const TrackerView = () => {
    const config = sectionConfig[currentSection];

    return (
      <div className="container">
        <div className="tracker-header">
          <div className="tracker-title-container">
            <span className="emoji">{config.emoji}</span>
            <h2 className="tracker-title">{config.title}</h2>
          </div>
          <span className="week-indicator">Week {currentWeek + 1}</span>
        </div>

        <div className="section-tabs">
          {Object.entries(sectionConfig).map(([key, conf]) => (
            <button
              key={key}
              onClick={() => setCurrentSection(key)}
              className={`section-tab ${currentSection === key ? 'active' : ''}`}
              style={currentSection === key ? { backgroundColor: conf.color } : {}}
            >
              <span className="tab-emoji">{conf.emoji}</span>
              <span className="tab-text">{conf.title}</span>
            </button>
          ))}
        </div>

        <div className="activities-container">
          {activities[currentSection].map((activity, activityIdx) => (
            <div key={activityIdx} className="activity-card">
              <h3 className="activity-name">{activity.name}</h3>
              <div className="days-grid">
                {days.map((day, dayIdx) => (
                  <div key={dayIdx} className="day-column">
                    <div className="day-label">{day}</div>
                    <CheckBox
                      checked={isChecked(currentSection, activityIdx, dayIdx)}
                      onToggle={() => toggleCheck(currentSection, activityIdx, dayIdx)}
                      disabled={!activity.days.includes(dayIdx)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const ProgressView = () => (
    <div className="container">
      <div className="progress-view-header">
        <h2 className="progress-view-title">Weekly Progress</h2>
        <div className="week-navigation">
          <button
            onClick={() => setCurrentWeek(Math.max(0, currentWeek - 1))}
            className="week-button"
          >
            ←
          </button>
          <span className="week-text">Week {currentWeek + 1}</span>
          <button
            onClick={() => setCurrentWeek(currentWeek + 1)}
            className="week-button"
          >
            →
          </button>
        </div>
      </div>

      <div className="progress-list">
        {Object.entries(sectionConfig).map(([key, config]) => {
          const progress = calculateProgress(key);
          return (
            <div key={key} className="progress-item">
              <div className="progress-item-header">
                <div className="progress-item-title">
                  <span className="emoji">{config.emoji}</span>
                  <span className="progress-item-name">{config.title}</span>
                </div>
                <span className="progress-item-percent">{progress}%</span>
              </div>
              <div className="progress-bar-container">
                <div
                  className="progress-bar"
                  style={{ width: `${progress}%`, backgroundColor: config.color }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="quote">
        <p className="quote-text">
          "Therefore honor God with your bodies." - 1 Corinthians 6:20
        </p>
      </div>
    </div>
  );

  const BottomNavigation = () => (
    <div className="bottom-nav">
      <button
        onClick={() => setCurrentView('home')}
        className={`nav-button ${currentView === 'home' ? 'active' : ''}`}
      >
        <span className="nav-button-text">🏠</span>
        <span className="nav-label">Home</span>
      </button>
      <button
        onClick={() => setCurrentView('tracker')}
        className={`nav-button ${currentView === 'tracker' ? 'active' : ''}`}
      >
        <span className="nav-button-text">📋</span>
        <span className="nav-label">Tracker</span>
      </button>
      <button
        onClick={() => setCurrentView('progress')}
        className={`nav-button ${currentView === 'progress' ? 'active' : ''}`}
      >
        <span className="nav-button-text">📊</span>
        <span className="nav-label">Progress</span>
      </button>
    </div>
  );

  return (
    <div className="app">
      {currentView === 'home' && <HomeView />}
      {currentView === 'tracker' && <TrackerView />}
      {currentView === 'progress' && <ProgressView />}
      <BottomNavigation />
    </div>
  );
};

const WellnessTrackerApp = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">🌱</div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <LoginForm />;
  }

  return <WellnessTrackerContent />;
};

const App = () => (
  <AuthProvider>
    <WellnessTrackerApp />
  </AuthProvider>
);

export default App;